<template>
    <div>
        <Transition v-if="isLoading"
                    name="spinner">
            <div id="cover-spin">
                <span class="loader">
                    <span>{{ $spinnerText }}</span>
                </span>

            </div>
        </Transition>

        <div class="flex min-h-screen bg-gray-800 overflow-x-hidden">
            <!-- Sidebar -->
            <aside :class="[
                'bg-gray-900 border-r border-gray-800 flex flex-col transition-all duration-200 overflow-x-hidden',
                (sidebarCollapsed ? 'w-18 min-w-0 max-w-[64px]' : 'w-56 min-w-0 max-w-[224px]'),
                isMobile ? 'w-16 min-w-0 max-w-[64px]' : ''
            ]"
                   @mouseenter="expandableMenu && !isMobile ? (sidebarCollapsed = false) : null"
                   @mouseleave="expandableMenu && !isMobile ? (sidebarCollapsed = true) : null">
                <div :class="['h-16 border-b border-gray-800 px-4', sidebarCollapsed ? 'flex justify-center items-center' : 'flex items-center justify-end']">
                    <button v-if="!expandableMenu || isMobile"
                            @click="async () => { sidebarCollapsed = !sidebarCollapsed; forceRerender = true; await nextTick(); forceRerender = false; }"
                            class="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 flex items-center justify-center h-10 w-10">
                        <svg v-if="!sidebarCollapsed"
                             class="h-6 w-6"
                             fill="none"
                             stroke="currentColor"
                             stroke-width="2"
                             viewBox="0 0 24 24"
                             stroke-linecap="round"
                             stroke-linejoin="round">
                            <path d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <svg v-else
                             class="h-6 w-6"
                             fill="none"
                             stroke="currentColor"
                             stroke-width="2"
                             viewBox="0 0 24 24"
                             stroke-linecap="round"
                             stroke-linejoin="round">
                            <path d="M9 6l6 6-6 6" />
                        </svg>
                    </button>
                </div>

                <nav class="flex-1 overflow-y-auto">
                    <PanelMenu v-if="!forceRerender"
                               :model="menuItems"
                               :class="[(expandableMenu || isMobile) && sidebarCollapsed ? 'w-16 px-0' : 'w-full px-0', 'bg-transparent border-0 text-gray-200 transition-all duration-200']"
                               :key="(expandableMenu || isMobile) ? sidebarCollapsed : false"
                               :style="(expandableMenu || isMobile) && sidebarCollapsed ? 'width:64px;min-width:64px;max-width:64px;' : 'width:100%;'"
                               :pt="{ menuItem: { class: (expandableMenu || isMobile) && sidebarCollapsed ? 'flex justify-center px-0 py-2 gap-0' : 'flex items-center gap-2 px-4 py-2' } }">
                        <template #item="{ item, props, hasSubmenu, active, level }">
                            <span v-if="(!item?.hidden || isCreator) && (Object.values(unprotectedMenuItems).includes(item?.fullPath?.toLowerCase().replace(/[_\s]/g, '-')) || hasPermission(item?.fullPath))">
                                <Link v-ripple
                                      v-if="item.route"
                                      :href="checkRoute(item.route, null, item.label)"
                                      :id="item.id"
                                      :style="item.itemstyle"
                                      :class="[
                                        'rounded-md hover:bg-white/5 hover:text-white transition-colors',
                                        sidebarCollapsed ? 'flex items-center justify-center px-0 py-2 gap-0' : 'flex items-center gap-2 px-4 py-2',
                                        sidebarCollapsed ? 'flex-col' : '',
                                        !sidebarCollapsed && level > 0 ? 'submenu-panel' : ''
                                    ]"
                                      v-bind="props.action"
                                      v-tooltip="sidebarCollapsed ? __(item.label) : null">
                                    <i :class="item.icon"
                                       :style="item.iconstyle"></i>
                                    <template v-if="sidebarCollapsed && hasSubmenu">
                                        <svg v-if="!expandedSubmenu[item.label]"
                                             xmlns="http://www.w3.org/2000/svg"
                                             class="h-3 w-3 mt-1 text-gray-400"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor"
                                             stroke-width="2">
                                            <path stroke-linecap="round"
                                                  stroke-linejoin="round"
                                                  d="M19 9l-7 7-7-7" />
                                        </svg>
                                        <svg v-else
                                             xmlns="http://www.w3.org/2000/svg"
                                             class="h-3 w-3 mt-1 text-gray-400"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor"
                                             stroke-width="2">
                                            <path stroke-linecap="round"
                                                  stroke-linejoin="round"
                                                  d="M5 15l7-7 7 7" />
                                        </svg>
                                    </template>
                                    <span v-if="!sidebarCollapsed"
                                          :class="item.labelclass"
                                          :style="item.labelstyle">{{ __(item.label)
                                        }}</span>
                                    <div v-if="item.description && !sidebarCollapsed"
                                         class="text-xs text-gray-400"><span v-html="item.description"></span>
                                    </div>
                                </Link>
                                <div v-ripple
                                     v-else
                                     :class="[
                                        'rounded-md hover:bg-white/5 hover:text-white transition-colors cursor-pointer',
                                        sidebarCollapsed ? 'flex items-center justify-center px-0 py-2 gap-0' : 'flex items-center gap-2 px-4 py-2',
                                        sidebarCollapsed ? 'flex-col' : '',
                                        !sidebarCollapsed && level > 0 ? 'submenu-panel' : ''
                                    ]"
                                     :style="item.itemstyle"
                                     v-bind="props.action"
                                     @click="stateOfSubmenu(hasSubmenu, item.label, active)"
                                     v-tooltip="sidebarCollapsed ? __(item.label) : null">
                                    <i :class="item.icon"
                                       :style="item.iconstyle"></i>
                                    <template v-if="sidebarCollapsed && hasSubmenu">
                                        <svg v-if="!expandedSubmenu[item.label]"
                                             xmlns="http://www.w3.org/2000/svg"
                                             class="h-3 w-3 mt-1 text-gray-400"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor"
                                             stroke-width="2">
                                            <path stroke-linecap="round"
                                                  stroke-linejoin="round"
                                                  d="M19 9l-7 7-7-7" />
                                        </svg>
                                        <svg v-else
                                             xmlns="http://www.w3.org/2000/svg"
                                             class="h-3 w-3 mt-1 text-gray-400"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor"
                                             stroke-width="2">
                                            <path stroke-linecap="round"
                                                  stroke-linejoin="round"
                                                  d="M5 15l7-7 7 7" />
                                        </svg>
                                    </template>
                                    <span v-if="!sidebarCollapsed"
                                          :class="item.labelclass"
                                          :style="item.labelstyle">{{ __(item.label)
                                        }}</span>
                                    <i v-if="hasSubmenu && !sidebarCollapsed"
                                       :class="['pi text-xs', expandedSubmenu[item.label] ? 'pi-angle-down' : 'pi-angle-right']"
                                       :style="item.submenustyle"></i>
                                    <div v-if="item.description && !sidebarCollapsed"
                                         class="text-xs text-gray-400"><span v-html="item.description"></span>
                                    </div>
                                </div>
                            </span>
                        </template>
                    </PanelMenu>
                </nav>
            </aside>
            <!-- Main content -->
            <main class="flex-1 flex flex-col min-h-0 bg-secondary-50 overflow-x-hidden">
                <!-- Header/topbar -->
                <header class="bg-gray-800 border-b border-gray-700 h-16 flex items-center px-6 justify-between">
                    <div class="flex items-center gap-4">
                        <img :src="menuLogoWhite"
                             class="h-8" />
                    </div>
                    <div class="flex items-center gap-4">
                        <AlertNotifier />
                        <button v-if="multiModuleApp"
                                type="button"
                                class="relative rounded-full p-1 text-gray-400 hover:text-white focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500"
                                @click="selectModule"
                                v-tooltip.bottom="__('Select module')">
                            <span class="sr-only">{{ __('Select module') }}</span>
                            <i class="pi pi-th-large"></i>
                        </button>
                        <div class="relative"
                             ref="topUserDropdownRef">
                            <button @click="showTopUserDropdown = !showTopUserDropdown"
                                    class="relative flex max-w-xs items-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500">
                                <span class="sr-only">Open user menu</span>
                                <div class="size-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm outline -outline-offset-1 outline-white/10">
                                    {{ userName?.charAt(0) || 'U' }}</div>
                            </button>
                            <div v-if="showTopUserDropdown"
                                 class="absolute right-0 mt-2 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-50 py-2 min-w-[200px]">
                                <div class="px-4 py-3 border-b border-gray-700">
                                    <div class="flex items-center gap-3">
                                        <div class="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white text-base font-medium">
                                            {{ userName?.charAt(0) || 'U' }}</div>
                                        <div class="flex-1 min-w-0">
                                            <div class="text-sm font-medium text-white truncate">{{ userName }}</div>
                                            <div class="text-xs text-gray-400 truncate">{{ userEmail }}</div>
                                        </div>
                                    </div>
                                </div>
                                <Link :href="checkRoute('user-profile', { user_id: userId })"
                                      class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors">
                                    <i class="pi pi-user text-gray-400"></i><span>Profil użytkownika</span>
                                </Link>
                                <button @click="saveSession"
                                        class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left"><i class="pi pi-save text-gray-400"></i><span>Zapisz sesję</span></button>
                                <button @click="removeSession"
                                        class="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left"><i class="pi pi-trash text-gray-400"></i><span>Usuń sesję</span></button>
                            </div>
                        </div>
                    </div>
                </header>
                <div class="flex-1 layout-box">
                    <slot></slot>
                </div>
                <footer class="bg-gray-700 py-2 shadow-inner mt-auto">
                    <div class="mx-auto px-4 sm:px-6 lg:px-8">
                        <div class="flex flex-col md:flex-row items-center justify-between gap-4">
                            <div class="flex items-center gap-3 text-gray-400 text-sm">
                                <img :src="developerLogo"
                                     class="h-6 w-auto opacity-80" />
                                <p class="pl-3 text-gray-200/40">© 4Evolve v1.0</p>
                                <p class="text-xs text-gray-200/40">rev {{ systemRevision }}</p>
                            </div>
                            <nav class="flex items-center gap-6 text-sm">
                                <Link :href="checkRoute('dashboard-help', null, null)"
                                      class="text-gray-400 hover:text-white transition-colors">Pomoc</Link>
                                <Link :href="checkRoute('dashboard-changelog', null, null)"
                                      class="text-gray-400 hover:text-white transition-colors">Changelog</Link>
                                <Link :href="checkRoute('dashboard-privacy', null, null)"
                                      class="text-gray-400 hover:text-white transition-colors">Polityka prywatności</Link>
                            </nav>
                        </div>
                    </div>
                </footer>
            </main>
        </div>

        <flash-message />
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeMount, onBeforeUnmount, watchEffect, nextTick } from 'vue'
import { Link, router, usePage } from '@inertiajs/vue3'
import PanelMenu from 'primevue/panelmenu';
import Button from 'primevue/button';
import Image from '@helper/Image.vue'
import { initFunctions } from "../../init.js";
import { __, saveAppSession, removeAppSession, getStoreValue, getSessionValue, hasPermission, checkRoute, setStoreValue, getServerValue } from '@pages/Helper/utils.js';
import { menuLogoWhite, developerLogo } from '@helper/constants.js'
import AlertNotifier from '../App/Data/Alert/AlertNotifier.vue';

const menuItems = ref(null);
const unprotectedMenuItems = ref({});
const userName = ref(null);
const userId = ref(null);
const userEmail = ref(null);
const multiModuleApp = ref(false);
const showTopUserDropdown = ref(false);
const topUserDropdownRef = ref(null);
const forceRerender = ref(false);

const toSlideStyle = ref("margin-left:0px;")
const itemVisibility = ref(true)
const submenuState = ref({})
const expandedSubmenu = ref({})
const systemCaption = ref(getStoreValue('appCaption'))
const systemRevision = ref(getStoreValue('systemRevision'))
const expandableMenu = ref(false)
const sidebarCollapsed = ref(false)
const isMobile = ref(false)

//const dataFromSession = ref(null);
//const currentDate = ref(null)
const page = usePage();
const isLoading = ref(false);
const isCreator = ref(false);

//------------------------------------------------------------------------------

// watchEffect(async () => {
//     let savedDataFromSession = null
//     savedDataFromSession = getStoreValue('dataFromSession')

//     if (!savedDataFromSession) {
//         savedDataFromSession = await getServerValue('dataFromSession')
//         if (savedDataFromSession?.hasOwnProperty('dataFromSession')) {
//             setStoreValue('dataFromSession', savedDataFromSession.dataFromSession)
//             savedDataFromSession = savedDataFromSession.dataFromSession
//         }
//     }

//     if (savedDataFromSession) {
//         dataFromSession.value = savedDataFromSession
//     }
// });

// //------------------------------------------------------------------------------

// watchEffect(async () => {
//     currentDate.value = page.props.currentDate;
// });

// ------------------------------------------------------------------------------

onBeforeMount(async () => {
    const layoutMenu = page.props.menuContent;

    if (layoutMenu && layoutMenu.menu) {
        menuItems.value = layoutMenu.menu;
        unprotectedMenuItems.value = layoutMenu.unprotected;
        userName.value = layoutMenu.userName;
        userId.value = layoutMenu.userId;
        userEmail.value = layoutMenu.userEmail;
        multiModuleApp.value = layoutMenu.multiModuleApp;

        if (menuItems.value.length == 0) {
            if (multiModuleApp.value) {
                console.error('Wybrany moduł (' + layoutMenu.moduleSymbol + ') nie posiada pliku json z zawartością menu. Nastąpi przekierowanie do strony wyboru modułu')
                window.location.href = route('select-module')
            } else {
                console.error('Aplikacja nie posiada pliku json z zawartością menu.')
                window.location.href = route('app-logout')
            }
        }
    }

    clearInterval(window.displaySessionInterval);
})

//------------------------------------------------------------------------------

onMounted(async () => {
    window.endOfSession = false
    window.showDisplaySessionTime = false;
    window.lastActivityTime = null;
    isLoading.value = true;
    await initFunctions();
    isLoading.value = false;
    isCreator.value = getStoreValue('isCreator')

    const elements = document.querySelectorAll('[aria-hidden]');
    elements.forEach(element => {
        //element.setAttribute('aria-hidden', 'false');
        element.removeAttribute('aria-hidden');
    });

    setExpandableMenu();

    document.addEventListener('mousedown', handleClickOutside);
    handleResize();
    window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside);
    window.removeEventListener('resize', handleResize);
});

const handleClickOutside = (event) => {
    if (topUserDropdownRef.value && !topUserDropdownRef.value.contains(event.target)) {
        showTopUserDropdown.value = false;
    }
};

//------------------------------------------------------------------------------

const stateOfSubmenu = (hasSubmenu, label, active) => {
    submenuState.value[label] = !active
    expandedSubmenu.value[label] = hasSubmenu && submenuState.value[label]
}

//------------------------------------------------------------------------------

const slideDrawer = (state) => {
    if (state) {
        itemVisibility.value = false
        toSlideStyle.value = 'margin-left:0;transition: margin-left 0.2s;'
        document.querySelector('.v-navigation-drawer__content').style.overflowY = 'hidden';
        document.querySelectorAll('.p-submenu-list').forEach((elem) => elem.style.display = "none")
    } else {
        itemVisibility.value = true
        toSlideStyle.value = 'margin-left:200px;transition: margin-left 0.2s;'
        document.querySelector('.v-navigation-drawer__content').style.overflowY = 'auto';
        document.querySelectorAll('.p-submenu-list').forEach((elem) => elem.style.display = "block")
    }
}

//------------------------------------------------------------------------------

const setExpandableMenu = () => {
    expandableMenu.value = Boolean(getSessionValue("user.isExpandableMenu"));
    if (isMobile.value) {
        sidebarCollapsed.value = true;
    } else if (!expandableMenu.value) {
        sidebarCollapsed.value = false;
    }
}

const handleResize = () => {
    isMobile.value = window.innerWidth < 768;
    setExpandableMenu();
}

//------------------------------------------------------------------------------

const saveSession = () => {
    saveAppSession();
}

//------------------------------------------------------------------------------

const removeSession = () => {
    removeAppSession();
}

//------------------------------------------------------------------------------

const selectModule = () => {
    router.get(route('select-module', { module: getStoreValue('currentModule') }))
}

</script>

<style lang='scss' scoped></style>
